import { clsx, Loader, useQuery } from "@gemlightbox/core-kit";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRequest } from "urql";
import { graphqlClient } from "src/api";
import {
  ORGANIZATION_UPDATE_ONBOARDING_SURVEY,
  USER_ONBOARDING_SURVEY_INFO,
} from "src/api/graphql-api/user/user.gql";
import { activateLifetimePlan } from "src/api/subscriptions";
import { ReactComponent as GemiqLogoLongForm } from "src/common/images/gemiq-logo-long-form.svg";

import { LanguageSelect } from "src/external-ts/components";
import { useStores } from "src/hooks";
import { config } from "src/config/environment";
import { LocaleCodeTypes } from "src/store/locale/locale-generated.store.types";

import { ReactComponent as CheckCircleIcon } from "../../assets/images/check-circle.svg";
import { ReactComponent as CheckDisabledIcon } from "../../assets/images/check-disabled.svg";
import { ReactComponent as CheckIcon } from "../../assets/images/check.svg";
import { ReactComponent as KeyIcon } from "../../assets/images/key.svg";
import { ReactComponent as UsersIcon } from "../../assets/images/users.svg";
import styles from "./lifetime-activate.page.module.scss";

enum ErrorCode {
  ACTIVATE_CODE_INVALID = "subscription:activate_code_invalid",
  ACTIVATE_CODE_EXPIRED = "subscription:activate_code_expired",
  ACTIVATE_FAILED = "subscription:activate_failed",
}

type SurveyField = "operatorType" | "primaryUseCase" | "jewelryType";

type SurveyOption = {
  label: string;
  value: string;
};

type SurveyOptionConfig = {
  labelKey: LocaleCodeTypes;
  defaultLabel: string;
  value: string;
};

type SurveyQuestion = {
  field: SurveyField;
  title: string;
  options: SurveyOption[];
};

type SurveyQuestionConfig = {
  field: SurveyField;
  titleKey: LocaleCodeTypes;
  defaultTitle: string;
  options: SurveyOptionConfig[];
};

type SurveyForm = Partial<Record<SurveyField, string>>;

type OnboardingSurveyInfo = {
  userInfo?: {
    additionalInfo?: {
      requireOnboardingSurvey?: boolean;
    };
    organization?: {
      id?: number;
      operatorType?: string | null;
      primaryUseCase?: string | null;
      jewelryType?: string | null;
    };
  };
};

const SURVEY_QUESTION_CONFIGS: SurveyQuestionConfig[] = [
  {
    field: "operatorType",
    titleKey: 'activate["lifetime-plan"]["onboarding-survey"].questions["operator-type"].title',
    defaultTitle: "What's your role in the business?",
    options: [
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["operator-type"].options["owner-self-use"]',
        defaultLabel: "I'm the owner — and I'll be using it",
        value: "owner_self_use",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["operator-type"].options["owner-team-use"]',
        defaultLabel: "I'm the owner — my team will be using it",
        value: "owner_team_use",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["operator-type"].options["team-member"]',
        defaultLabel: "My manager or owner set this up for me",
        value: "team_member",
      },
    ],
  },
  {
    field: "primaryUseCase",
    titleKey: 'activate["lifetime-plan"]["onboarding-survey"].questions["primary-use-case"].title',
    defaultTitle: "Where do you primarily sell?",
    options: [
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["primary-use-case"].options["own-website"]',
        defaultLabel: "Through my website or online store",
        value: "own_website",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["primary-use-case"].options["social-media"]',
        defaultLabel: "Through Instagram or social media",
        value: "social_media",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["primary-use-case"].options["direct-messaging"]',
        defaultLabel: "Directly to clients via WhatsApp or email",
        value: "direct_messaging",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["primary-use-case"].options["in-store"]',
        defaultLabel: "In-store or at trade shows",
        value: "in_store",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["primary-use-case"].options.marketplace',
        defaultLabel: "On marketplaces (Amazon, 1stDibs etc.)",
        value: "marketplace",
      },
    ],
  },
  {
    field: "jewelryType",
    titleKey: 'activate["lifetime-plan"]["onboarding-survey"].questions["jewelry-type"].title',
    defaultTitle: "What do you primarily photograph?",
    options: [
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["jewelry-type"].options["fine-jewelry"]',
        defaultLabel: "Fine jewelry (necklaces, earrings, bracelets, rings)",
        value: "fine_jewelry",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["jewelry-type"].options["diamonds-gemstones"]',
        defaultLabel: "Loose diamonds & gemstones (natural, lab-grown, coloured stones)",
        value: "diamonds_gemstones",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["jewelry-type"].options["antique-vintage-watches"]',
        defaultLabel: "Antique, vintage & watches (estate, heritage, fashion timepieces)",
        value: "antique_vintage_watches",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["jewelry-type"].options["fashion-silver"]',
        defaultLabel: "Fashion & silver (costume, silver)",
        value: "fashion_silver",
      },
      {
        labelKey:
          'activate["lifetime-plan"]["onboarding-survey"].questions["jewelry-type"].options.other',
        defaultLabel: "Other",
        value: "other",
      },
    ],
  },
];

const shouldShowOnboardingSurvey = (data?: OnboardingSurveyInfo) => {
  const userInfo = data?.userInfo;
  const organization = userInfo?.organization;

  return (
    userInfo?.additionalInfo?.requireOnboardingSurvey === true &&
    !!organization?.id &&
    !organization.operatorType &&
    !organization.primaryUseCase &&
    !organization.jewelryType
  );
};

export const LifetimeActivatePage: React.FC = observer(() => {
  const { subscriptionsStore, userStore, localeStore } = useStores();
  const isProPlan =
    subscriptionsStore.isProMonthlySubscription || subscriptionsStore.isProYearlySubscription;

  const [submitting, setSubmitting] = useState(false);
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>();
  const [errMsg, setErrMsg] = useState<string>();
  const [surveyErrMsg, setSurveyErrMsg] = useState<string>();
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyForm, setSurveyForm] = useState<SurveyForm>({});
  const [surveyOrganizationId, setSurveyOrganizationId] = useState<number>();
  const [surveyStep, setSurveyStep] = useState(0);

  // Prefill the activation code from the URL (e.g. the GemIQ activation email button
  // links to `/lifetime-activate?...&activation_code=<CODE>`, forwarded into this iframe).
  const { query } = useQuery();
  const prefilledActivationCode =
    typeof query.activation_code === "string" ? query.activation_code : "";
  const debugOnboardingSurveyParam = query.debug_onboarding_survey;
  const shouldDebugOnboardingSurvey =
    debugOnboardingSurveyParam === "" ||
    debugOnboardingSurveyParam === "1" ||
    debugOnboardingSurveyParam === "true";
  const hasDebuggedOnboardingSurvey = useRef(false);

  const surveyQuestions = useMemo<SurveyQuestion[]>(
    () =>
      SURVEY_QUESTION_CONFIGS.map((question) => ({
        field: question.field,
        title: localeStore.t(question.titleKey, question.defaultTitle),
        options: question.options.map((option) => ({
          label: localeStore.t(option.labelKey, option.defaultLabel),
          value: option.value,
        })),
      })),
    [localeStore, localeStore.currentLocale],
  );
  const currentSurveyQuestion = surveyQuestions[surveyStep];

  const loadOnboardingSurveyState = useCallback(async () => {
    try {
      const res = await graphqlClient
        .executeQuery(createRequest(USER_ONBOARDING_SURVEY_INFO, {}))
        .toPromise();
      const data = res.data as OnboardingSurveyInfo | undefined;

      if (res.error || !shouldShowOnboardingSurvey(data)) {
        setShowSurvey(false);
        setSurveyOrganizationId(undefined);
        return;
      }

      setShowSurvey(true);
      setSurveyStep(0);
      setSurveyOrganizationId(data?.userInfo?.organization?.id);
    } catch (err) {
      setShowSurvey(false);
      setSurveyOrganizationId(undefined);
    }
  }, []);

  useEffect(() => {
    if (
      !shouldDebugOnboardingSurvey ||
      hasDebuggedOnboardingSurvey.current ||
      userStore.loadingMe ||
      subscriptionsStore.isLoadingSubscription
    ) {
      return;
    }

    hasDebuggedOnboardingSurvey.current = true;
    setSubmitSuccess(true);
    setErrMsg("");
    setSurveyErrMsg("");
    setShowContactSupport(false);
    setShowSurvey(false);
    loadOnboardingSurveyState();
  }, [
    loadOnboardingSurveyState,
    shouldDebugOnboardingSurvey,
    subscriptionsStore.isLoadingSubscription,
    userStore.loadingMe,
  ]);

  const handleSubmit = async (form: FormData) => {
    setSubmitting(true);
    setShowContactSupport(false);
    setSurveyErrMsg("");
    setShowSurvey(false);
    try {
      const fetchResult = await activateLifetimePlan.getRequest({ data: form }).fetch();
      if (fetchResult.status === "success") {
        setSubmitSuccess(true);
        if (errMsg) setErrMsg("");
        await loadOnboardingSurveyState();
      } else {
        setSubmitSuccess(false);
        if (fetchResult.status !== "error") {
          setErrMsg(
            fetchResult.error.formattedMessage ||
              localeStore.t(
                'activate["lifetime-plan"]["network-error"]',
                "Network error, please retry or check your network connection.",
              ),
          );
        } else {
          const { code, message } = fetchResult.error.originalError;
          setErrMsg(
            code === ErrorCode.ACTIVATE_CODE_INVALID
              ? localeStore.t(
                  'activate["lifetime-plan"].error["invalid-code"]',
                  "The activation code is not valid.",
                )
              : message ||
                  localeStore.t(
                    'activate["lifetime-plan"]["something-wrong"]',
                    "Something went wrong.",
                  ),
          );
          setShowContactSupport(code !== ErrorCode.ACTIVATE_CODE_INVALID);
        }
      }
      setSubmitting(false);
    } catch (err) {
      setSubmitting(false);
    }
  };

  const submitSurvey = async (nextSurveyForm: SurveyForm) => {
    const isNextSurveyReady = SURVEY_QUESTION_CONFIGS.every(({ field }) => !!nextSurveyForm[field]);
    if (!isNextSurveyReady || !surveyOrganizationId) return;

    setSurveySubmitting(true);
    setSurveyErrMsg("");
    try {
      const res = await graphqlClient
        .executeMutation(
          createRequest(ORGANIZATION_UPDATE_ONBOARDING_SURVEY, {
            organizationId: surveyOrganizationId,
            input: {
              operatorType: nextSurveyForm.operatorType,
              primaryUseCase: nextSurveyForm.primaryUseCase,
              jewelryType: nextSurveyForm.jewelryType,
            },
          }),
        )
        .toPromise();

      if (res.error || !res.data?.organizationUpdate?.id) {
        setSurveyErrMsg(
          localeStore.t(
            'activate["lifetime-plan"]["onboarding-survey"]["submit-error"]',
            "Something went wrong. Please try again.",
          ),
        );
        return;
      }

      setShowSurvey(false);
      await subscriptionsStore.loadOrganization();
    } catch (err) {
      setSurveyErrMsg(
        localeStore.t(
          'activate["lifetime-plan"]["onboarding-survey"]["submit-error"]',
          "Something went wrong. Please try again.",
        ),
      );
    } finally {
      setSurveySubmitting(false);
    }
  };

  const handleSurveyOptionSelect = (field: SurveyField, value: string) => {
    if (surveySubmitting) return;

    const nextSurveyForm = { ...surveyForm, [field]: value };
    setSurveyForm(nextSurveyForm);
    setSurveyErrMsg("");

    if (surveyStep < SURVEY_QUESTION_CONFIGS.length - 1) {
      setSurveyStep((prev) => prev + 1);
      return;
    }

    submitSurvey(nextSurveyForm);
  };

  const handleSurveyBack = () => {
    if (surveySubmitting || surveyStep === 0) return;
    setSurveyErrMsg("");
    setSurveyStep((prev) => prev - 1);
  };

  const renderSurveyDialog = () => {
    if (!currentSurveyQuestion) return null;

    return (
      <div className={styles.surveyDialogOverlay} role="dialog" aria-modal="true">
        <section className={styles.surveyDialog}>
          <GemiqLogoLongForm className={styles.surveyLogo} />
          <div className={styles.surveyProgressRow}>
            {surveyQuestions.map((question, index) => (
              <div
                className={clsx(styles.surveyProgressSegment, index <= surveyStep && styles.active)}
                key={question.field}
              />
            ))}
          </div>
          <div className={styles.surveyStepCount}>
            {localeStore.t('activate["lifetime-plan"]["onboarding-survey"].step', "Step")}{" "}
            <span>{surveyStep + 1}</span>{" "}
            {localeStore.t('activate["lifetime-plan"]["onboarding-survey"].of', "of")}{" "}
            {surveyQuestions.length}
          </div>
          <h2 className={styles.surveyDialogTitle}>{currentSurveyQuestion.title}</h2>
          <div className={styles.surveyDialogOptions}>
            {currentSurveyQuestion.options.map((option) => (
              <button
                className={clsx(
                  styles.surveyDialogOption,
                  surveyForm[currentSurveyQuestion.field] === option.value && styles.selected,
                )}
                disabled={surveySubmitting}
                key={option.value}
                onClick={() => handleSurveyOptionSelect(currentSurveyQuestion.field, option.value)}
                type="button"
              >
                <span className={styles.surveyRadio} />
                <span>{option.label}</span>
              </button>
            ))}
          </div>
          {surveyErrMsg && <div className={styles.surveyDialogError}>{surveyErrMsg}</div>}
          {surveySubmitting && (
            <div className={styles.surveySubmitting}>
              {localeStore.t(
                'activate["lifetime-plan"]["onboarding-survey"].submitting',
                "Submitting...",
              )}
            </div>
          )}
          <button
            className={clsx(styles.surveyBackButton, surveyStep === 0 && styles.disabled)}
            disabled={surveyStep === 0 || surveySubmitting}
            onClick={handleSurveyBack}
            type="button"
          >
            <span className={styles.surveyBackIcon} aria-hidden="true" />
            {localeStore.t('activate["lifetime-plan"]["onboarding-survey"].back', "Back")}
          </button>
        </section>
      </div>
    );
  };
  const renderHeader = () => {
    return (
      <div className={styles.logoContainer}>
        <GemiqLogoLongForm className={styles.pageLogo} />
      </div>
    );
  };
  const renderFooter = () => {
    return (
      <div className={styles.footer}>
        <LanguageSelect
          className={styles.langSelect}
          inputWrapperClassName={styles.langSelectInputWrapper}
        />
        <a
          href="https://picupmedia.com/contact-us#support"
          style={{ fontWeight: 500 }}
          target="_blank"
          rel="noreferrer"
        >
          {localeStore.t('activate["lifetime-plan"].help', "Help")}
        </a>
      </div>
    );
  };

  if (userStore.loadingMe || subscriptionsStore.isLoadingSubscription) {
    return <Loader type="goo-loader" position="absolute" />;
  }

  if (isProPlan && !shouldDebugOnboardingSurvey) {
    return (
      <div className={styles.container}>
        {renderHeader()}
        <div className={styles.accountProblem}>
          <UsersIcon />
          <div className={styles.content}>
            {localeStore.t(
              'activate["lifetime-plan"].error["pro-plan"]',
              "Your account is already pro subscription and cannot be activated. Please contact customer service.",
            )}
          </div>
          <a href="https://picupmedia.com/contact-us#support" target="_blank" rel="noreferrer">
            <button className={styles.button}>
              {localeStore.t('activate["lifetime-plan"]["contact-us"]', "Contact Us")}
            </button>
          </a>
        </div>
        {renderFooter()}
      </div>
    );
  }

  if (userStore.isSubaccount) {
    return (
      <div className={styles.container}>
        {renderHeader()}
        <div className={styles.accountProblem}>
          <UsersIcon />
          <div className={styles.content}>
            {localeStore.t(
              'activate["lifetime-plan"].error.subaccount',
              "Your account is part of a master account and cannot be activated. Please contact customer service.",
            )}
          </div>
          <a href="https://picupmedia.com/contact-us#support" target="_blank" rel="noreferrer">
            <button className={styles.button}>
              {localeStore.t('activate["lifetime-plan"]["contact-us"]', "Contact Us")}
            </button>
          </a>
        </div>
        {renderFooter()}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {renderHeader()}
      <div
        className={styles.title}
        dangerouslySetInnerHTML={{
          __html: localeStore.t(
            'activate["lifetime-plan"].title',
            "Activate your <b>GemIQ</b> subscription to unlock more features. Just follow these simple steps:",
          ),
        }}
      />
      <div className={clsx(styles.stepContainer, submitSuccess && styles.active)}>
        <div className={styles.stepIndicator}>
          <div className={styles.stepIcon}>
            <KeyIcon />
          </div>
          <div className={styles.stepLine} />
        </div>
        <div className={styles.stepBody}>
          <div className={styles.stepTitle}>
            {localeStore.t(
              'activate["lifetime-plan"]["step1-title"]',
              "Enter your activation code",
            )}
          </div>
          <form
            className={styles.stepContent}
            style={{ marginBottom: 44 }}
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.currentTarget);
              // validate form data here
              handleSubmit(form);
            }}
          >
            <div className={clsx(styles.inputWrapper, submitSuccess === false && styles.error)}>
              <input
                required
                disabled={submitSuccess}
                name="code"
                defaultValue={prefilledActivationCode}
                className={styles.input}
                placeholder="0000-0000-0000-0000"
              />
              {submitSuccess && <CheckCircleIcon />}
            </div>
            {errMsg && (
              <div className={styles.errMsg}>
                {errMsg}&nbsp;
                {showContactSupport && (
                  <a
                    href="https://picupmedia.com/contact-us#support"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {localeStore.t(
                      'activate["lifetime-plan"]["contact-support"]',
                      "contact support",
                    )}
                  </a>
                )}
              </div>
            )}
            {!submitSuccess && (
              <>
                <p className={styles.codeHelp}>
                  {localeStore.t(
                    'activate["lifetime-plan"]["code-help"]',
                    "Can't find your code? Check your inbox and spam for the GemIQ activation email, or",
                  )}{" "}
                  <a
                    href="https://picupmedia.com/contact-us#support"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {localeStore.t(
                      'activate["lifetime-plan"]["contact-support"]',
                      "contact support",
                    )}
                  </a>
                  .
                </p>
                <button type="submit" disabled={submitting} className={styles.button}>
                  {localeStore.t('activate["lifetime-plan"].activate', "Activate")}
                  {submitting && (
                    <span
                      className="lds-dual-ring"
                      style={{ verticalAlign: "middle", marginLeft: 8 }}
                    />
                  )}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
      <div className={styles.stepContainer}>
        <div className={styles.stepIndicator}>
          <div className={styles.stepIcon}>
            {submitSuccess ? <CheckIcon /> : <CheckDisabledIcon />}
          </div>
        </div>
        <div className={styles.stepBody}>
          <div className={styles.stepTitle} style={{ marginBottom: 16 }}>
            {localeStore.t('activate["lifetime-plan"].enjoy', "Enjoy access to more features")}
          </div>
          <div className={styles.stepContent}>
            {submitSuccess && !showSurvey && (
              <a
                href={window.$platform.isCompact ? "javascript:void(0);" : "/"}
                onClick={() => userStore.parentPostMessageRedirect(config.gemhubUrl)}
              >
                <button className={styles.button}>
                  {localeStore.t('activate["lifetime-plan"].goto', "Go to GemIQ")}
                </button>
              </a>
            )}
          </div>
        </div>
      </div>
      {renderFooter()}
      {showSurvey && renderSurveyDialog()}
    </div>
  );
});

export default LifetimeActivatePage;
